import { Box } from '@chakra-ui/react';
import SidebarMenu from './SidebarMenu';

interface AppNavRailProps {
	isOverlayNav: boolean;
	isNavOpen: boolean;
	onCloseNav: () => void;
	sidebarWidthTransition: string;
	effectiveIsSidebarCollapsed: boolean;
	toggleSidebarCollapse: () => void;
}

/**
 * Left navigation.
 * - Laptop/desktop: a plain flex child of the shell row — the main pane
 *   (flex 1) reflows automatically as the rail collapses/expands.
 * - Mobile (< laptop): a fixed overlay drawer under the top bar, opened from
 *   the hamburger, so content keeps the full viewport width.
 */
const AppNavRail = ({
	isOverlayNav,
	isNavOpen,
	onCloseNav,
	sidebarWidthTransition,
	effectiveIsSidebarCollapsed,
	toggleSidebarCollapse,
}: AppNavRailProps) => {
	const menu = (
		<SidebarMenu
			isCollapsed={isOverlayNav ? false : effectiveIsSidebarCollapsed}
			onToggleCollapse={toggleSidebarCollapse}
			currentWidth="sidebar"
			showCollapseToggle={!isOverlayNav}
			onNavigateItem={isOverlayNav ? onCloseNav : undefined}
		/>
	);

	if (isOverlayNav) {
		return (
			<>
				<Box
					position="fixed"
					top="var(--partbay-topbar)"
					left={0}
					right={0}
					bottom={0}
					bg="blackAlpha.500"
					zIndex="overlay"
					opacity={isNavOpen ? 1 : 0}
					pointerEvents={isNavOpen ? 'auto' : 'none'}
					transition="opacity 180ms ease"
					onClick={onCloseNav}
					aria-hidden={!isNavOpen}
				/>
				<Box
					as="aside"
					id="partbay-nav-drawer"
					aria-label="Main navigation"
					aria-hidden={!isNavOpen}
					position="fixed"
					top="var(--partbay-topbar)"
					left={0}
					bottom={0}
					w="sidebar"
					maxW="85vw"
					zIndex="overlay"
					transform={isNavOpen ? 'translateX(0)' : 'translateX(-100%)'}
					transition="transform 180ms ease"
					borderRight="1px solid"
					borderColor="gray.300"
					overflow="hidden"
					bg="gray.100"
				>
					{menu}
				</Box>
			</>
		);
	}

	return (
		<Box
			as="aside"
			flexShrink={0}
			w={effectiveIsSidebarCollapsed ? 'sidebarCollapsed' : 'sidebar'}
			h="100%"
			overflow="hidden"
			transition={sidebarWidthTransition}
			borderRight="1px solid"
			borderColor="gray.300"
			bg="gray.100"
		>
			{/* Inner box stays expanded-width so content doesn't squish mid-collapse. */}
			<Box w="sidebar" h="100%">
				{menu}
			</Box>
		</Box>
	);
};

export default AppNavRail;
